import { setNotification } from "features/Notification/notificationSlice";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

const InboxContext = createContext({});

export const InboxProvider = ({ children }) => {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);

  const [activeLabelTab, setActiveLabelTab] = useState("inbox");

  const [emailGrants, setEmailGrants] = useState([]);
  const [selectedGrantEmail, setSelectedGrantEmail] = useState(null);

  const [labels, setLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [inboxLabel, setInboxLabel] = useState(null);
  const [defaultLabels, setDefaultLabels] = useState([]);

  const [threadId, setThreadId] = useState(null);
  const [fromEmail, setFromEmail] = useState(null);
  const [pageTokensList, setPageTokensList] = useState([]);
  const [emailsList, setEmailsList] = useState([]);
  const [emailsHistoryList, setEmailsHistoryList] = useState([]);
  const [refreshEmails, setRefreshEmails] = useState(false);

  const [messageId, setMessageId] = useState(null);

  const emailIdsList = useMemo(
    () => emailsList.map((email) => email.messageId),
    [emailsList],
  );

  /*   All Functions
   ********************************************* */

  // reset All
  const resetAll = () => {
    setSelectedLabel(null);
    setThreadId(null);
    setMessageId(null);
    setFromEmail(null);
    setPageTokensList([]);
    setEmailsList([]);
    // setRefreshEmails(false);
    setMessageId(null);
  };

  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  // get Grant Emails
  const getGrantEmails = useCallback(async () => {
    try {
      loadingOn();

      const { data } = await API.post(
        "/email-grants?page=1&limit=20",
        {},
        { headers: authHeader() },
      );

      if (data?.success && data?.hasGrant) {
        setEmailGrants(data?.data || []);
        // set email with isDefault true as selectedGrantEmail
        const defaultGrant = data?.data.find(
          (grant) => grant.isDefault === true,
        );
        setSelectedGrantEmail(defaultGrant?.email || null);
      } else if (!data?.hasGrant) {
        navigate("/email/inbox?auth=required");
      } else {
        dispatch(
          setNotification({
            message: "Failed to fetch grant emails",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      loadingOff();
    }
  }, [dispatch]);

  // get labels for selectedGrantEmail
  const getLabels = useCallback(async () => {
    if (!selectedGrantEmail) return;

    try {
      loadingOn();
      // const { data } = await API.get("/emails/labels/list", {
      //   headers: authHeader(),
      // });
      const { data } = await API.get("/emails/labels/list", {
        headers: authHeader(),
      });

      if (data?.success && data?.labels) {
        setLabels(data?.labels);
        if (user.roles === "admin" || user.permissions.includes("show_inbox")) {
          setSelectedLabel("INBOX");
        } else {
          // set first label as selectedLabel
          setSelectedLabel(data?.labels[0]?.id || null);
        }
        setDefaultLabels(
          [
            ...data?.defaultFolders,
            {
              labelId: "ALL_MAILS",
              name: "All Mail",
              unreadCount:
                data?.labels.find((label) => label.labelId === "ALL_MAILS")
                  ?.unreadCount || 0,
            },
          ] || [],
        );
        // setInboxLabel(data?.inbox);
      } else if (!data?.hasGrant) {
        setSearchParams({
          auth: "required",
        });
      } else {
        console.log(data);
        setLabels([]);
        dispatch(
          setNotification({
            message: "Failed to fetch labels",
            type: "error",
          }),
        );
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  }, [dispatch, navigate, selectedGrantEmail]);

  // get labelName from labelId
  const getLabelName = (labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label ? label.name : "Unknown Label";
  };

  // get filtered Labels that are in labels array and send array of label names
  const getFilteredLabelNames = (
    labelIds,
    folder = false,
    emailHistory = false,
  ) => {
    const filteredLabels = labels.filter((label) =>
      labelIds.includes(label.labelId),
    );

    // Accommodate for Inbox, Sent, Trash
    // if (labelIds.includes("INBOX")) {
    //   filteredLabels.push({ labelId: "INBOX", name: "Inbox" });
    // }
    // if (labelIds.includes("SENT")) {
    //   filteredLabels.push({ labelId: "SENT", name: "Sent" });
    // }

    // sort filteredLabels based on sortId
    filteredLabels.sort((a, b) => a.sortId - b.sortId);

    // Ensure Order Inbox Sent ,Trash at start
    if (labelIds.includes("TRASH")) {
      filteredLabels.unshift({ labelId: "TRASH", name: "Trash" });
    }
    if (folder !== "SENT") {
      if (labelIds.includes("SENT")) {
        filteredLabels.unshift({ labelId: "SENT", name: "Sent" });
      }
    }

    if (labelIds.includes("INBOX") && emailHistory) {
      filteredLabels.unshift({ labelId: "INBOX", name: "Inbox" });
    }
    if (labelIds.includes("SPAM")) {
      filteredLabels.unshift({ labelId: "SPAM", name: "Spam" });
    }

    return filteredLabels.map((label) =>
      label.name === "archived_mails" ? "Archived" : label.name,
    );
  };

  const getSenderName = (email) => {
    let direction = "from";
    if (selectedLabel === "SENT") {
      direction = "to";
    }

    let name = "";
    if (email?.[direction][0]?.name) {
      name = email?.[direction][0]?.name;
    } else if (email?.[direction][0]?.email === selectedGrantEmail) {
      name = "me";
    } else {
      name = email?.[direction][0]?.email.split("@")[0];
    }
    return name;
  };

  const formatGmailDate = (emailDate) => {
    // Gmail timestamp is in SECONDS → convert to milliseconds
    const date = new Date(emailDate * 1000);

    // Time: HH:MM (24-hour)
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Date: MMMM-DD-YYYY
    const datePart = date
      .toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
      .replace(",", "");

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const messageDate = new Date(date);
    messageDate.setHours(0, 0, 0, 0);

    // Today
    if (messageDate.getTime() === today.getTime()) {
      return `${timePart} - Today`;
    }

    // Yesterday
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (messageDate.getTime() === yesterday.getTime()) {
      return `${timePart} - Yesterday`;
    }

    return `${timePart} - ${datePart}`;
  };

  // Set Emails as Read or Unread
  const handleBulkReadStatus = async (messageIdsArray, isRead) => {
    try {
      const { data } = await API.post(
        `/emails/mark-read-unread`,
        { messageIdsArray, isRead },
        { headers: authHeader() },
      );
      if (data?.success) {
        // dispatch(
        //   setNotification({
        //     message: isRead
        //       ? "Emails marked as read"
        //       : "Emails marked as unread",
        //     type: "success",
        //   })
        // );
        return true;
      } else {
        dispatch(
          setNotification({
            message: "Failed to update email status",
            type: "error",
          }),
        );
        return false;
      }

      // Update local state or refetch emails
    } catch (error) {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        }),
      );
      return false;
    }
  };

  // Handle Bulk Delete Emails
  const handleBulkDelete = async (messageIdsArray) => {
    try {
      const { data } = await API.post(
        `/emails/delete-messages`,
        { messageIdsArray },
        { headers: authHeader() },
      );
      if (data?.success) {
        return true;
      } else {
        throw new Error("Failed to delete emails");
      }
      // Update local state or refetch emails
    } catch (error) {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        }),
      );
      return false;
    }
  };

  // Handle Bulk Move Emails
  const handleBulkMove = async (messageIdsArray, labelId) => {
    try {
      const { data } = await API.post(
        `/emails/move-to-label`,
        { messageIdsArray, labelId },
        { headers: authHeader() },
      );
      if (data?.success) {
        return true;
      } else {
        throw new Error("Failed to delete emails");
      }
      // Update local state or refetch emails
    } catch (error) {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        }),
      );
      return false;
    }
  };

  /*   All UseEffects *************************** */

  const inboxValues = {
    emailGrants,
    setEmailGrants,
    selectedGrantEmail,
    setSelectedGrantEmail,
    refreshGrantEmails: getGrantEmails,

    emailsHistoryList,
    setEmailsHistoryList,

    activeLabelTab,
    setActiveLabelTab,

    labels,
    loading,
    loadingOn,
    loadingOff,
    selectedLabel,
    setSelectedLabel,
    inboxLabel,
    setInboxLabel,
    refreshLabels: getLabels,

    threadId,
    setThreadId,
    getSenderName,
    formatGmailDate,
    getLabelName,
    getFilteredLabelNames,
    handleBulkReadStatus,
    handleBulkDelete,
    pageTokensList,
    setPageTokensList,
    handleBulkMove,

    messageId,
    setMessageId,
    emailsList,
    setEmailsList,
    emailIdsList,
    refreshEmails,
    setRefreshEmails,
    defaultLabels,
    setDefaultLabels,
    resetAll,
    fromEmail,
    setFromEmail,
  };

  return (
    <InboxContext.Provider value={inboxValues}>
      {children}
    </InboxContext.Provider>
  );
};

export default function useInbox() {
  return useContext(InboxContext);
}
